import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Bell, 
  Plus, 
  Clock, 
  Repeat, 
  MoreVertical, 
  Check, 
  Trash2, 
  Edit2, 
  Pause, 
  Play,
  ChevronRight,
  X,
  User,
  AlertTriangle
} from 'lucide-react';
import { Button, Modal } from '../components/common';
import { modalScrollLock } from '../utils/modalScrollLock';
import { Header } from '../components/layout/Header';
import { showToast } from '../components/common/Toast';
import { reminderApi, userApi } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { usePageSeen } from '../hooks/usePageSeen';

// Types
interface RecurringRule {
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  interval: number;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  endDate?: string;
  maxOccurrences?: number;
}

interface Reminder {
  id: string;
  title: string;
  description: string | null;
  remindAt: string;
  recurringRule: RecurringRule | null;
  creatorId: string;
  recipientId: string;
  isCompleted: boolean;
  completedAt: string | null;
  isPaused: boolean;
  taskId: string | null;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  recipient: {
    id: string;
    firstName: string;
    lastName: string;
    telegramChatId: string | null;
  };
  task?: {
    id: string;
    title: string;
  } | null;
  notifications: {
    id: string;
    channel: string;
    status: string;
    scheduledFor: string;
  }[];
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
}

const RemindersPage: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [filter, setFilter] = useState<'pending' | 'completed' | 'active'>('active');
  const [users, setUsers] = useState<User[]>([]);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingReminder, setDeletingReminder] = useState<Reminder | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // REV-1 fix: Mark page as seen after content loads
  usePageSeen({ hasLoadedContent: !loading });

  // Form state
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    remindAt: string;
    recipientId: string;
    taskId: string;
    isRecurring: boolean;
    recurringRule: {
      frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
      interval: number;
      daysOfWeek: number[];
      dayOfMonth: number;
      endDate: string;
    };
  }>({
    title: '',
    description: '',
    remindAt: '',
    recipientId: '',
    taskId: '',
    isRecurring: false,
    recurringRule: {
      frequency: 'DAILY',
      interval: 1,
      daysOfWeek: [],
      dayOfMonth: 1,
      endDate: '',
    },
  });

  useEffect(() => {
    loadReminders();
    loadUsers();
  }, [filter]);

  const loadReminders = async () => {
    try {
      setLoading(true);
      const result = await reminderApi.list({ status: filter });
      if (result.success) {
        setReminders(result.data as Reminder[]);
      }
    } catch (err: any) {
      showToast(t('reminders.error.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const result = await userApi.getUsersListNames();
      if (result.success) {
        setUsers(result.data as User[]);
      }
    } catch (err: any) {
      console.error('Failed to load users:', err);
      showToast(t('reminders.error.failedToLoadUsers'), 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      remindAt: '',
      recipientId: '',
      taskId: '',
      isRecurring: false,
      recurringRule: {
        frequency: 'DAILY',
        interval: 1,
        daysOfWeek: [],
        dayOfMonth: 1,
        endDate: '',
      },
    });
    setEditingReminder(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const data: any = {
        title: formData.title,
        description: formData.description || null,
        remindAt: formData.remindAt,
        recipientId: formData.recipientId || undefined,
        taskId: formData.taskId || undefined,
      };

      if (formData.isRecurring) {
        data.recurringRule = {
          frequency: formData.recurringRule.frequency,
          interval: formData.recurringRule.interval,
          daysOfWeek: formData.recurringRule.daysOfWeek.length > 0 ? formData.recurringRule.daysOfWeek : undefined,
          dayOfMonth: formData.recurringRule.frequency === 'MONTHLY' ? formData.recurringRule.dayOfMonth : undefined,
          endDate: formData.recurringRule.endDate || undefined,
        };
      }

      if (editingReminder) {
        const result = await reminderApi.update(editingReminder.id, data);
        if (result.success) {
          showToast(t('reminders.updated'), 'success');
          loadReminders();
        }
      } else {
        const result = await reminderApi.create(data);
        if (result.success) {
          showToast(t('reminders.created'), 'success');
          loadReminders();
        }
      }

      resetForm();
    } catch (err: any) {
      showToast(err.response?.data?.error?.message || t('reminders.error.failedToSave'), 'error');
    }
  };

  const handleComplete = async (reminderId: string) => {
    try {
      // Optimistic update - mark as completed locally first
      setReminders(prev => prev.map(r => 
        r.id === reminderId 
          ? { ...r, isCompleted: true, completedAt: new Date().toISOString() }
          : r
      ));
      setActiveMenu(null);
      
      const result = await reminderApi.complete(reminderId);
      if (result.success) {
        showToast(t('reminders.completed'), 'success');
        
        // If viewing pending reminders, the completed one will be removed after a delay
        // This gives the user visual feedback that it was completed
        if (filter === 'pending') {
          setTimeout(() => {
            loadReminders();
          }, 800);
        } else {
          loadReminders();
        }
      } else {
        // Revert on failure
        loadReminders();
      }
    } catch (err: any) {
      showToast(t('reminders.error.failedToComplete'), 'error');
      loadReminders(); // Reload to reset state
    }
  };

  const openDeleteModal = (reminder: Reminder) => {
    setDeletingReminder(reminder);
    setShowDeleteModal(true);
    setActiveMenu(null);
  };

  const handleDelete = async () => {
    if (!deletingReminder) return;

    setIsDeleting(true);
    try {
      await reminderApi.delete(deletingReminder.id);
      showToast(t('reminders.deleted'), 'success');
      loadReminders();
      setShowDeleteModal(false);
      setDeletingReminder(null);
    } catch (err: any) {
      showToast(t('reminders.error.failedToDelete'), 'error');
    }
    setIsDeleting(false);
  };

  const handlePause = async (reminderId: string) => {
    try {
      const result = await reminderApi.pause(reminderId);
      if (result.success) {
        showToast(t('reminders.paused'), 'success');
        loadReminders();
      }
    } catch (err: any) {
      showToast(t('reminders.error.failedToPause'), 'error');
    }
    setActiveMenu(null);
  };

  const handleResume = async (reminderId: string) => {
    try {
      const result = await reminderApi.resume(reminderId);
      if (result.success) {
        showToast(t('reminders.resumed'), 'success');
        loadReminders();
      }
    } catch (err: any) {
      showToast(t('reminders.error.failedToResume'), 'error');
    }
    setActiveMenu(null);
  };

  const handleSnooze = async (reminderId: string, minutes: number) => {
    try {
      const result = await reminderApi.snooze(reminderId, minutes);
      if (result.success) {
        showToast(t('reminders.snoozedFor', { minutes }), 'success');
        loadReminders();
      }
    } catch (err: any) {
      showToast(t('reminders.error.failedToSnooze'), 'error');
    }
    setActiveMenu(null);
  };

  const handleEdit = (reminder: Reminder) => {
    setEditingReminder(reminder);
    setFormData({
      title: reminder.title,
      description: reminder.description || '',
      remindAt: reminder.remindAt.slice(0, 16), // Format for datetime-local
      recipientId: reminder.recipientId,
      taskId: reminder.taskId || '',
      isRecurring: !!reminder.recurringRule,
      recurringRule: reminder.recurringRule ? {
        frequency: reminder.recurringRule.frequency,
        interval: reminder.recurringRule.interval || 1,
        daysOfWeek: reminder.recurringRule.daysOfWeek || [],
        dayOfMonth: reminder.recurringRule.dayOfMonth || 1,
        endDate: reminder.recurringRule.endDate || '',
      } : {
        frequency: 'DAILY',
        interval: 1,
        daysOfWeek: [],
        dayOfMonth: 1,
        endDate: '',
      },
    });
    setShowForm(true);
    setActiveMenu(null);
  };

  // Scroll lock — matches the inline modal in JSX (showForm), not the <Modal> component
  useEffect(() => {
    if (showForm) {
      modalScrollLock.register();
    }
    return () => {
      if (showForm) {
        modalScrollLock.unregister();
      }
    };
  }, [showForm]);

  const toggleDayOfWeek = (day: number) => {
    const days = formData.recurringRule.daysOfWeek;
    if (days.includes(day)) {
      setFormData({
        ...formData,
        recurringRule: {
          ...formData.recurringRule,
          daysOfWeek: days.filter(d => d !== day),
        },
      });
    } else {
      setFormData({
        ...formData,
        recurringRule: {
          ...formData.recurringRule,
          daysOfWeek: [...days, day].sort(),
        },
      });
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const minutes = Math.round(diff / 60000);
    const hours = Math.round(diff / 3600000);
    const days = Math.round(diff / 86400000);

    if (minutes < 0) return t('reminders.overdue');
    if (minutes < 60) return t('reminders.inMinutes', { minutes });
    if (hours < 24) return t('reminders.inHours', { hours });
    if (days === 1) return t('reminders.tomorrow');
    if (days < 7) return t('reminders.inDays', { days });
    return formatDateTime(dateStr);
  };

  const getDayNames = () => [t('reminders.daySun'), t('reminders.dayMon'), t('reminders.dayTue'), t('reminders.dayWed'), t('reminders.dayThu'), t('reminders.dayFri'), t('reminders.daySat')];

  const getFrequencyLabel = (rule: RecurringRule) => {
    switch (rule.frequency) {
      case 'DAILY':
        return rule.interval === 1 ? t('reminders.daily') : t('reminders.everyDays', { interval: rule.interval });
      case 'WEEKLY':
        if (rule.daysOfWeek && rule.daysOfWeek.length > 0) {
          const dayNames = getDayNames();
          const days = rule.daysOfWeek.map(d => dayNames[d]).join(', ');
          return t('reminders.weeklyOn', { days });
        }
        return rule.interval === 1 ? t('reminders.weekly') : t('reminders.everyWeeks', { interval: rule.interval });
      case 'MONTHLY':
        return rule.interval === 1 ? t('reminders.monthlyOnDay', { day: rule.dayOfMonth }) : t('reminders.everyMonths', { interval: rule.interval });
      default:
        return t('reminders.recurring');
    }
  };

  const isOwnReminder = (reminder: Reminder) => reminder.recipientId === user?.id;

  return (
    <div className="flex flex-col h-full">
      <Header title={t('reminders.title')} />
      
      <main className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Actions */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600 dark:text-gray-400">
              {t('reminders.subtitle')}
            </p>
            <Button 
              variant="primary" 
              onClick={() => setShowForm(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              {t('reminders.newReminder')}
            </Button>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mb-6" role="group" aria-label={t('reminders.filterAriaLabel')}>
            {([
              { key: 'pending' as const, label: t('reminders.filterPending') },
              { key: 'completed' as const, label: t('reminders.filterCompleted') },
              { key: 'active' as const, label: t('reminders.filterActive') },
            ]).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-3 sm:py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors ${
                  filter === key
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                aria-pressed={filter === key}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Reminder Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
              <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {editingReminder ? t('reminders.editReminder') : t('reminders.newReminder')}
                    </h2>
                    <button 
                      onClick={resetForm}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('reminders.titleLabel')} *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        required
                        placeholder={t('reminders.titlePlaceholder')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('reminders.descriptionLabel')}
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        rows={3}
                        placeholder={t('reminders.descriptionPlaceholder')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('reminders.dateTimeLabel')} *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.remindAt}
                        onChange={(e) => setFormData({ ...formData, remindAt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('reminders.assignToLabel')}
                      </label>
                      <select
                        value={formData.recipientId}
                        onChange={(e) => setFormData({ ...formData, recipientId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">{t('reminders.myself')}</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Recurring options */}
                    <div className="border-t dark:border-gray-700 pt-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.isRecurring}
                          onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('reminders.repeatReminder')}
                        </span>
                      </label>

                      {formData.isRecurring && (
                        <div className="mt-4 space-y-4 pl-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('reminders.frequencyLabel')}
                            </label>
                            <select
                              value={formData.recurringRule.frequency}
                              onChange={(e) => setFormData({
                                ...formData,
                                recurringRule: { ...formData.recurringRule, frequency: e.target.value as any },
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                              <option value="DAILY">{t('reminders.daily')}</option>
                              <option value="WEEKLY">{t('reminders.weekly')}</option>
                              <option value="MONTHLY">{t('reminders.monthly')}</option>
                            </select>
                          </div>

                          {/* Interval */}
                          <div className="flex items-center gap-3">
                            <label className="text-sm text-gray-600 dark:text-gray-400">{t('reminders.every')}</label>
                            <input
                              type="number"
                              min={1}
                              max={99}
                              value={formData.recurringRule.interval}
                              onChange={(e) => setFormData({
                                ...formData,
                                recurringRule: { ...formData.recurringRule, interval: parseInt(e.target.value) || 1 },
                              })}
                              className="w-16 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {formData.recurringRule.frequency === 'DAILY' && (formData.recurringRule.interval === 1 ? t('reminders.day') : t('reminders.days'))}
                              {formData.recurringRule.frequency === 'WEEKLY' && (formData.recurringRule.interval === 1 ? t('reminders.week') : t('reminders.weeks'))}
                              {formData.recurringRule.frequency === 'MONTHLY' && (formData.recurringRule.interval === 1 ? t('reminders.month') : t('reminders.months'))}
                            </span>
                          </div>

                          {formData.recurringRule.frequency === 'WEEKLY' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('reminders.daysOfWeekLabel')}
                              </label>
                              <div className="flex gap-1">
                                {getDayNames().map((day, index) => (
                                  <button
                                    key={day}
                                    type="button"
                                    onClick={() => toggleDayOfWeek(index)}
                                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                      formData.recurringRule.daysOfWeek.includes(index)
                                        ? 'bg-primary-500 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                    }`}
                                  >
                                    {day[0]}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {formData.recurringRule.frequency === 'MONTHLY' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('reminders.dayOfMonthLabel')}
                              </label>
                              <input
                                type="number"
                                min={1}
                                max={31}
                                value={formData.recurringRule.dayOfMonth}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  recurringRule: { ...formData.recurringRule, dayOfMonth: parseInt(e.target.value) },
                                })}
                                className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              />
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('reminders.endDateLabel')}
                            </label>
                            <input
                              type="date"
                              value={formData.recurringRule.endDate}
                              onChange={(e) => setFormData({
                                ...formData,
                                recurringRule: { ...formData.recurringRule, endDate: e.target.value },
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
                      <Button variant="secondary" onClick={resetForm} className="w-full sm:w-auto">
                        {t('common.cancel')}
                      </Button>
                      <Button variant="primary" type="submit" className="w-full sm:w-auto">
                        {editingReminder ? t('reminders.updateReminder') : t('reminders.createReminder')}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Reminders list */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">{t('reminders.loading')}</div>
            </div>
          ) : reminders.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('reminders.emptyTitle')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {filter === 'pending' 
                  ? t('reminders.emptyPending') 
                  : filter === 'active'
                  ? t('reminders.emptyActive')
                  : t('reminders.emptyCompleted')}
              </p>
              <Button variant="primary" onClick={() => setShowForm(true)}>
                {t('reminders.createReminder')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 overflow-visible">
              {reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border ${
                    reminder.isCompleted 
                      ? 'border-gray-200 dark:border-gray-700' 
                      : new Date(reminder.remindAt) < new Date()
                      ? 'border-red-300 dark:border-red-800'
                      : 'border-gray-200 dark:border-gray-700'
                  } p-4 relative overflow-visible`}
                >
                  <div className="flex items-start gap-4">
                    {/* Complete button - show for pending tab and triggered reminders in active tab */}
                    {(filter === 'pending' || (filter === 'active' && new Date(reminder.remindAt) <= new Date() && !reminder.isCompleted)) && (
                      <button
                        onClick={() => handleComplete(reminder.id)}
                        disabled={reminder.isCompleted}
                        className={`mt-1 min-h-[44px] min-w-[44px] rounded-full border-2 flex items-center justify-center transition-colors ${
                          reminder.isCompleted
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 dark:border-gray-600 hover:border-primary-500'
                        }`}
                      >
                        {reminder.isCompleted && <Check className="w-4 h-4" />}
                      </button>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium ${
                              reminder.isCompleted && !reminder.recurringRule
                                ? 'text-gray-500 dark:text-gray-400 line-through' 
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {reminder.title}
                            </h3>
                            {reminder.recurringRule && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                <Repeat className="w-3 h-3" />
                                {getFrequencyLabel(reminder.recurringRule)}
                              </span>
                            )}
                          </div>
                          {reminder.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {reminder.description}
                            </p>
                          )}
                          {/* Detailed recurrence info for active tab */}
                          {filter === 'active' && reminder.recurringRule && (
                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-2">
                              {reminder.recurringRule.interval > 1 && (
                                <span>{t('reminders.intervalEvery', { interval: reminder.recurringRule.interval, unit: reminder.recurringRule.frequency.toLowerCase().slice(0, -2) })}</span>
                              )}
                              {reminder.recurringRule.endDate && (
                                <span>{t('reminders.endsLabel')} {new Date(reminder.recurringRule.endDate).toLocaleDateString()}</span>
                              )}
                              {reminder.recurringRule.maxOccurrences && (
                                <span>{t('reminders.maxOccurrences', { count: reminder.recurringRule.maxOccurrences })}</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Actions menu - only available in Active tab */}
                        {filter === 'active' && (
                          <div className="relative">
                            <button
                              onClick={() => setActiveMenu(activeMenu === reminder.id ? null : reminder.id)}
                              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors cursor-pointer"
                            >
                              <MoreVertical className="w-5 h-5" />
                            </button>

                            {activeMenu === reminder.id && (
                              <div className="absolute right-0 mt-1 w-[calc(100vw-2rem)] sm:w-48 sm:max-w-none max-h-[50vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[1000] isolate transform origin-top-right">
                                <button
                                  onClick={() => handleEdit(reminder)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  {t('common.edit')}
                                </button>
                                {reminder.recurringRule && (
                                  <button
                                    onClick={() => reminder.isPaused ? handleResume(reminder.id) : handlePause(reminder.id)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                  >
                                    {reminder.isPaused ? (
                                      <>
                                        <Play className="w-4 h-4" />
                                        {t('reminders.resume')}
                                      </>
                                    ) : (
                                      <>
                                        <Pause className="w-4 h-4" />
                                        {t('reminders.pause')}
                                      </>
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => openDeleteModal(reminder)}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 cursor-pointer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {t('common.delete')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Meta info */}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className={`flex items-center gap-1 ${
                          new Date(reminder.remindAt) < new Date() && !reminder.isCompleted
                            ? 'text-red-600 dark:text-red-400'
                            : ''
                        }`}>
                          <Clock className="w-4 h-4" />
                          {getRelativeTime(reminder.remindAt)}
                        </span>

                        {/* Snooze buttons - show for pending tab and triggered reminders in active tab */}
                        {(filter === 'pending' || (filter === 'active' && new Date(reminder.remindAt) <= new Date())) && !reminder.isCompleted && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleSnooze(reminder.id, 5)}
                              className="text-xs px-3 py-2 sm:py-1 rounded min-h-[44px] bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors"
                              title={t('reminders.snooze5Min')}
                            >
                              +5m
                            </button>
                            <button
                              onClick={() => handleSnooze(reminder.id, 30)}
                              className="text-xs px-3 py-2 sm:py-1 rounded min-h-[44px] bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors"
                              title={t('reminders.snooze30Min')}
                            >
                              +30m
                            </button>
                          </div>
                        )}

                        {/* Recurrence info - only show in pending/completed tabs (active tab shows it in badge) */}
                        {filter !== 'active' && reminder.recurringRule && !reminder.isCompleted && (
                          <span className="flex items-center gap-1">
                            <Repeat className="w-4 h-4" />
                            {getFrequencyLabel(reminder.recurringRule)}
                          </span>
                        )}

                        {reminder.isPaused && (
                          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                            <Pause className="w-4 h-4" />
                            {t('reminders.pausedBadge')}
                          </span>
                        )}

                        {!isOwnReminder(reminder) && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {t('reminders.forUser', { firstName: reminder.recipient.firstName, lastName: reminder.recipient.lastName })}
                          </span>
                        )}

                        {/* Show creator if different from current user */}
                        {reminder.creatorId !== user?.id && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {t('reminders.byUser', { firstName: reminder.creator.firstName, lastName: reminder.creator.lastName })}
                          </span>
                        )}

                        {reminder.task && (
                          <span className="flex items-center gap-1">
                            <ChevronRight className="w-4 h-4" />
                            {t('reminders.taskLabel')}: {reminder.task.title}
                          </span>
                        )}
                      </div>

                      {/* Notification channels */}
                      {reminder.notifications.length > 0 && (
                        <div className="flex gap-2 mt-3">
                          {Array.from(new Set(reminder.notifications.map(n => n.channel))).map((channel) => (
                            <span 
                              key={channel}
                              className={`text-xs px-2 py-1 rounded ${
                                channel === 'TELEGRAM' 
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                  : channel === 'IN_APP'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              {channel === 'IN_APP' ? t('reminders.channelWeb') : channel}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setDeletingReminder(null);
            }}
            title={t('reminders.deleteReminderTitle')}
            size="sm"
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('reminders.deleteThisReminder')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {t('reminders.deleteConfirmation', { title: deletingReminder?.title })}
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
                <Button variant="secondary" onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingReminder(null);
                }} className="w-full sm:w-auto">
                  {t('common.cancel')}
                </Button>
                <Button 
                  variant="danger" 
                  onClick={handleDelete}
                  isLoading={isDeleting}
                  className="w-full sm:w-auto"
                >
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          </Modal>

          {/* Click outside to close menu */}
          {activeMenu && (
            <div 
              className="fixed inset-0 z-0" 
              onClick={() => setActiveMenu(null)}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default RemindersPage;
